/**
 * What a BASE v2.0 cycle is doing, and what its ladder actually is.
 *
 * Two things on this screen can mislead an operator into acting, and both are
 * about the difference between an order and an intention.
 *
 * The first is the cycle's own state. A cycle sitting at −22 % with its
 * recovery armed is not the same as one that has already averaged down, and it
 * is not the same as one holding because the rules forbid adding more. Those
 * are three different amounts of money at risk and three different next
 * events, so they are three different words here.
 *
 * The second is the ladder. The normal grid is resting limit orders: they exist
 * at the venue and will fill without anybody deciding anything. The recovery
 * zones are not orders at all — they are prices at which the strategy starts
 * watching. Drawing them the same way would tell an operator the account has
 * committed capital it has not committed, which is the most expensive
 * misreading this screen can cause.
 *
 * Nothing here decides anything. The state and the rung statuses are the
 * server's; this module only maps them to words and refuses to guess when the
 * server sent something it does not know.
 */

/** What the cycle is doing, as the runtime reports it. */
export type CycleState =
  | "NORMAL"
  | "RECOVERY_ARMED"
  | "TRACKING_LOCAL_LOW"
  | "DCA_CANDIDATE"
  | "RECOVERY_HOLD";

const CYCLE_STATES: readonly CycleState[] = [
  "NORMAL",
  "RECOVERY_ARMED",
  "TRACKING_LOCAL_LOW",
  "DCA_CANDIDATE",
  "RECOVERY_HOLD",
];

export const CYCLE_STATE_LABEL: Record<CycleState, string> = {
  NORMAL: "обычный",
  RECOVERY_ARMED: "восстановление взведено",
  TRACKING_LOCAL_LOW: "следит за минимумом",
  DCA_CANDIDATE: "готов докупить",
  RECOVERY_HOLD: "удержание",
};

/**
 * How loudly each state reads.
 *
 * Only the states that need a person are coloured. A cycle trading normally is
 * the majority of the table, and a table where everything is marked is a table
 * where nothing is.
 */
export const CYCLE_STATE_TONE: Record<CycleState, "muted" | "warning" | "danger"> = {
  NORMAL: "muted",
  RECOVERY_ARMED: "warning",
  TRACKING_LOCAL_LOW: "warning",
  DCA_CANDIDATE: "warning",
  RECOVERY_HOLD: "danger",
};

/**
 * The state the server named, or nothing.
 *
 * Nothing, rather than a guess. An unrecognised state shown as `NORMAL` would
 * be this screen quietly reporting calm it has no evidence for.
 */
export function cycleStateOf(raw: string | null | undefined): CycleState | null {
  if (raw == null) return null;
  const found = CYCLE_STATES.find((state) => state === raw);
  return found ?? null;
}

/**
 * What one rung of the ladder is.
 *
 * `order` is placed at the venue and can fill on its own. `zone` is a price the
 * strategy watches; no capital is committed to it and nothing will happen at it
 * without a further decision by the runtime.
 */
export type RungKind = "order" | "zone";

export type RungStatus =
  | "FILLED"
  | "PARTIALLY_FILLED"
  | "RESTING"
  | "CANCELLED"
  | "WAITING"
  | "ARMED"
  | "TRACKING"
  | "DCA_READY";

export const RUNG_STATUS_LABEL: Record<RungStatus, string> = {
  FILLED: "исполнен",
  PARTIALLY_FILLED: "исполнен частично",
  RESTING: "выставлен",
  CANCELLED: "снят",
  WAITING: "ожидает",
  ARMED: "взведена",
  TRACKING: "следит",
  DCA_READY: "готова к докупке",
};

/**
 * Which statuses belong to which kind.
 *
 * Stated rather than assumed, so a zone can never be described with a word that
 * implies an order exists.
 */
export const ORDER_STATUSES: readonly RungStatus[] = [
  "FILLED",
  "PARTIALLY_FILLED",
  "RESTING",
  "CANCELLED",
];

export const ZONE_STATUSES: readonly RungStatus[] = [
  "WAITING",
  "ARMED",
  "TRACKING",
  "DCA_READY",
];

export function kindOfStatus(status: RungStatus): RungKind {
  return ORDER_STATUSES.includes(status) ? "order" : "zone";
}

/** One rung, whichever kind it is. */
export type Rung = {
  readonly index: number;
  readonly kind: RungKind;
  readonly status: RungStatus;
  /** The price, as the server stated it. Absent while it is not known. */
  readonly price: string | null;
  /** The distance from entry the contract names this rung by, e.g. "−20 %". */
  readonly offset: string | null;
};

/**
 * What a recovering cycle has spent and what it has left.
 *
 * Budgets are two, not one. The normal budget buys the grid; the recovery
 * reserve is held back for the zones and is not available to the grid or to any
 * other slot. Reporting one number for both would make a slot look like it had
 * money to work with that is already committed to its own worst case.
 */
export type RecoveryReading = {
  readonly state: CycleState;
  readonly vwap: string | null;
  readonly invested_margin: string | null;
  readonly normal_budget_used: string | null;
  readonly normal_budget_remaining: string | null;
  readonly recovery_reserve_used: string | null;
  readonly recovery_reserve_remaining: string | null;
  /** The next zone the cycle would arm, and where the price has to go. */
  readonly next_zone: string | null;
  readonly local_low: string | null;
  readonly retracement_needed: string | null;
  readonly strategy_version: string | null;
};

/**
 * The reading a slot payload carries, or nothing.
 *
 * Read defensively: the running-slot payload does not carry these fields yet,
 * and widening the generated type would be this build claiming the server
 * promises something it has not.
 */
export function recoveryOf(slot: object): RecoveryReading | null {
  const record = slot as Record<string, unknown>;
  const state = cycleStateOf(
    typeof record.cycle_state === "string" ? record.cycle_state : null,
  );
  if (state === null) return null;
  const text = (key: string): string | null => {
    const raw = record[key];
    return typeof raw === "string" && raw !== "" ? raw : null;
  };
  return {
    state,
    vwap: text("vwap"),
    invested_margin: text("margin"),
    normal_budget_used: text("normal_budget_used"),
    normal_budget_remaining: text("normal_budget_remaining"),
    recovery_reserve_used: text("recovery_reserve_used"),
    recovery_reserve_remaining: text("recovery_reserve_remaining"),
    next_zone: text("next_activation_zone"),
    local_low: text("local_low"),
    retracement_needed: text("retracement_needed"),
    strategy_version: text("strategy_version"),
  };
}
